import * as React from 'react';

import {
    faChevronDoubleLeft,
    faChevronDoubleRight,
    faChevronLeft,
    faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import { DAY_OF_WEEK_TRANSLATIONS_MAP } from 'src/features/pricingPlan/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import {
    CALCULATION_TYPE,
    PRICING_SIMULATION_ROW_KIND,
    PricingSimulation,
    PricingSimulationRow,
    PricingSimulationTier,
} from 'src/features/pricingPlan/utils/types';
import { DataTable } from 'src/registry/components/table/DataTable';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { tableMessages } from 'src/registry/components/table/utils/messages';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import { Input } from 'src/registry/ui/Input';
import { CURRENCY } from 'src/types';
import { commonMessages } from 'src/utils/messages';

const TIME_FORMAT = 'HH:mm';
const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 24 * MINUTES_IN_HOUR;
const DAY_FORMAT = 'DD.MM';
// The day a row belongs to - its start, by the local time of the browser
const DAY_KEY_FORMAT = 'YYYY-MM-DD';

// The API sends the times of a band with their seconds, while a band is read by the minute: "05:59:59" is "05:59".
const formatHour = (time: string) => time.slice(0, 5);

const columnHelper = createColumnHelper<PricingSimulationRow>();

type PricingSimulationTableProps = {
    simulation?: PricingSimulation;
    isLoading: boolean;
    // The version the screen is simulating - a row priced by another one says so
    simulatedPlanId: string;
};

/**
 * The breakdown of a simulated stay, a day per page: a long period would otherwise be one endless scroll, and the
 * hours are easier to follow when the date is in the title instead of in every row. The columns follow the
 * calculation type - a per fixed tick plan charges by the rate band a tick starts in, so every row names its band.
 * The table is informative only: no actions, no filters.
 */
function PricingSimulationTable({ simulation, isLoading, simulatedPlanId }: PricingSimulationTableProps) {
    const { formatMessage, formatNumber, locale } = useIntl();

    const currency: CURRENCY | undefined = simulation?.currency;
    const periodStart: string | undefined = simulation?.start;

    const formatAmount = React.useCallback(
        (amount: number) =>
            currency ? formatNumber(amount / 100, { style: 'currency', currency }) : (amount / 100).toFixed(2),
        [currency, formatNumber],
    );

    // "1 day, 10 hours and 20 minutes" - the parts that are not zero, the last one joined with "and"
    const formatDuration = React.useCallback(
        (totalMinutes: number) => {
            const days = Math.floor(totalMinutes / MINUTES_IN_DAY);
            const hours = Math.floor((totalMinutes % MINUTES_IN_DAY) / MINUTES_IN_HOUR);
            const minutes = totalMinutes % MINUTES_IN_HOUR;

            const parts: string[] = [];

            if (days) parts.push(formatMessage(commonMessages.days, { value: days }));
            if (hours) parts.push(formatMessage(commonMessages.hours, { value: hours }));
            if (minutes || !parts.length) parts.push(formatMessage(commonMessages.minutes, { value: minutes }));

            if (parts.length === 1) return parts[0];

            return `${parts.slice(0, -1).join(', ')} ${formatMessage(pricingPlanMessages.durationAnd)} ${parts[parts.length - 1]}`;
        },
        [formatMessage],
    );

    const isFixedTick = simulation?.calculation_type === CALCULATION_TYPE.PER_FIXED_TICK;
    // Another tariff - or a newer version of this one - may take over in the middle of the period. A row says which
    // version priced it whenever that is not the one the screen simulates, and whenever several of them are involved
    const severalPlans = new Set((simulation?.rows ?? []).map((row) => row.pricing_plan?.id).filter(Boolean)).size > 1;
    const isTotal = simulation?.calculation_type === CALCULATION_TYPE.PER_PERIODS_TOTAL;

    // "up to 4 hours", "4 hours - 8 hours", "over 8 hours" - the duration band a tier covers
    const formatTier = React.useCallback(
        (tier: PricingSimulationTier) => {
            if (!tier.start_minute && tier.end_minute === null) {
                return formatMessage(pricingPlanMessages.tierWholeStay);
            }

            if (!tier.start_minute) {
                return formatMessage(pricingPlanMessages.tierUpTo, { duration: formatDuration(tier.end_minute!) });
            }

            if (tier.end_minute === null) {
                return formatMessage(pricingPlanMessages.tierOver, { duration: formatDuration(tier.start_minute) });
            }

            return `${formatDuration(tier.start_minute)} - ${formatDuration(tier.end_minute)}`;
        },
        [formatDuration, formatMessage],
    );

    // The rows grouped by the day they start in, in the order the breakdown has them
    const days = React.useMemo(() => {
        const grouped = new Map<string, PricingSimulationRow[]>();

        (simulation?.rows ?? []).forEach((row) => {
            const key = dayjs(row.start).format(DAY_KEY_FORMAT);
            grouped.set(key, [...(grouped.get(key) ?? []), row]);
        });

        return [...grouped.entries()].map(([key, rows]) => ({ key, label: dayjs(key).format(DAY_FORMAT), rows }));
    }, [simulation]);

    const [dayIndex, setDayIndex] = React.useState(0);

    // A new breakdown starts from its first day
    React.useEffect(() => setDayIndex(0), [simulation]);

    const currentDay = days[dayIndex];

    // The day number as it is typed - the same handling the pagination of the other tables has for the page number
    const [typedDay, setTypedDay] = React.useState('1');

    React.useEffect(() => setTypedDay((dayIndex + 1).toString()), [dayIndex]);

    const goToDay = React.useCallback(
        (day: number) => setDayIndex(Math.min(Math.max(day, 0), Math.max(days.length - 1, 0))),
        [days.length],
    );

    const onChangedDayInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value);

        if (value < 1) setTypedDay('1');
        else if (value > days.length) setTypedDay(days.length.toString());
        else setTypedDay(event.target.value);
    };

    // The typed day is applied on blur, as the other tables do it
    const onBlurredDayInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value);

        if (isNaN(value) || !value) setTypedDay((dayIndex + 1).toString());
        else goToDay(value - 1);
    };

    const onDayInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.code === 'Enter' && typedDay) goToDay(Number(typedDay) - 1);
    };

    // What the chosen day adds to the stay - for the per periods total type the rows carry the change of its price,
    // so the sum of them is what that day costs either way
    const dayTotal: number = (currentDay?.rows ?? []).reduce((total, row) => total + row.amount_with_tax, 0);

    const columns = React.useMemo(
        () => [
            columnHelper.accessor('start', {
                header: DataTableColumnHeader,
                cell: ({ row }) => dayjs(row.original.start).format(TIME_FORMAT),
                meta: { title: formatMessage(pricingPlanMessages.fromHour) },
            }),
            columnHelper.accessor('end', {
                header: DataTableColumnHeader,
                cell: ({ row }) => {
                    const start = dayjs(row.original.start);
                    const end = dayjs(row.original.end);

                    return (
                        <div className="flex items-center gap-1">
                            {end.format(TIME_FORMAT)}
                            {/* A tick may run into the next day - the date is then named, so that the hour reads right */}
                            {!end.isSame(start, 'day') && (
                                <span className="text-xs text-gray-500">{end.format(DAY_FORMAT)}</span>
                            )}
                        </div>
                    );
                },
                meta: { title: formatMessage(pricingPlanMessages.toHour) },
            }),
            columnHelper.display({
                // A per fixed tick plan prices by the rate band a tick starts in, the two per periods types by the
                // tier the duration has reached - the column names whichever priced the row
                id: 'priced_by',
                header: DataTableColumnHeader,
                cell: ({ row }) => {
                    const { kind, tick_rate, tier, pricing_plan } = row.original;

                    const plan = pricing_plan && (severalPlans || pricing_plan.id !== simulatedPlanId) && (
                        <span className="text-xs text-gray-500">
                            {pricing_plan.translations.find((translation) => translation.language === locale)?.name ??
                                ''}{' '}
                            {formatMessage(pricingPlanMessages.simulationVersion, {
                                number: pricing_plan.version_number,
                            })}
                        </span>
                    );

                    if (kind === PRICING_SIMULATION_ROW_KIND.NO_PLAN) {
                        return <Badge theme="gray">{formatMessage(pricingPlanMessages.simulationNoPlanRow)}</Badge>;
                    }

                    if (kind === PRICING_SIMULATION_ROW_KIND.FREE_TIME) {
                        return (
                            <div className="flex flex-col items-start gap-1">
                                <Badge theme="blue">{formatMessage(pricingPlanMessages.simulationFreeTimeRow)}</Badge>
                                {plan}
                            </div>
                        );
                    }

                    if (!tick_rate && !tier) {
                        return <Badge theme="gray">{formatMessage(pricingPlanMessages.simulationNoRateRow)}</Badge>;
                    }

                    const label = tick_rate
                        ? `${formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[tick_rate.start_day])} ${formatHour(
                              tick_rate.from_time,
                          )} - ${formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[tick_rate.end_day])} ${formatHour(tick_rate.to_time)}`
                        : formatTier(tier!);

                    return (
                        <div className="flex flex-col items-start gap-1">
                            <span>{label}</span>
                            {kind === PRICING_SIMULATION_ROW_KIND.CAPPED && (
                                <Badge theme="amber">{formatMessage(pricingPlanMessages.simulationCappedRow)}</Badge>
                            )}
                            {plan}
                        </div>
                    );
                },
                meta: {
                    title: formatMessage(
                        isFixedTick ? pricingPlanMessages.rateBand : pricingPlanMessages.simulationTier,
                    ),
                },
            }),
            columnHelper.accessor('amount_with_tax', {
                header: DataTableColumnHeader,
                cell: ({ row }) => formatAmount(row.original.amount_with_tax),
                // What the rows of the day add up to - the same amount the title names. Without a day there is
                // nothing to sum up and the table renders its empty state, so no footer row is defined at all.
                footer: currentDay
                    ? () => formatMessage(pricingPlanMessages.simulationDayTotal, { total: formatAmount(dayTotal) })
                    : undefined,
                meta: {
                    // The per periods total type re-prices the whole stay, so what a row carries is a change of its
                    // price - it may even be a drop, when a cheaper tier is reached
                    title: formatMessage(
                        isTotal ? pricingPlanMessages.simulationChange : pricingPlanMessages.simulationAmount,
                    ),
                },
            }),
            columnHelper.accessor('total_with_tax', {
                header: DataTableColumnHeader,
                cell: ({ row }) => <span className="font-medium">{formatAmount(row.original.total_with_tax)}</span>,
                meta: { title: formatMessage(pricingPlanMessages.simulationTotal) },
            }),
            columnHelper.display({
                id: 'total_duration',
                header: DataTableColumnHeader,
                // How long the stay has lasted by the end of this row - the counterpart of the running total
                cell: ({ row }) =>
                    periodStart ? formatDuration(dayjs(row.original.end).diff(dayjs(periodStart), 'minute')) : '-',
                meta: { title: formatMessage(pricingPlanMessages.simulationTotalDuration) },
            }),
        ],
        [
            currentDay,
            dayTotal,
            formatAmount,
            formatDuration,
            formatMessage,
            formatTier,
            isFixedTick,
            isTotal,
            locale,
            periodStart,
            severalPlans,
            simulatedPlanId,
        ],
    );

    // The breakdown comes as one payload - the table renders the chosen day of it, without server paging
    const data = React.useMemo(
        () => ({ count: currentDay?.rows.length ?? 0, results: currentDay?.rows ?? [] }),
        [currentDay],
    );

    return (
        <div className="flex flex-1 flex-col gap-4">
            <DataTable<PricingSimulationRow, any>
                isLoading={isLoading}
                columns={columns}
                data={data}
                enableSearchParams={false}
                semantics={{
                    title: currentDay
                        ? formatMessage(pricingPlanMessages.simulationBreakdownForDay, { day: currentDay.label })
                        : formatMessage(pricingPlanMessages.simulationBreakdown),
                    entryName: formatMessage(pricingPlanMessages.simulationRow).toLowerCase(),
                    entryNames: formatMessage(pricingPlanMessages.simulationRows).toLowerCase(),
                }}
            />
            {days.length > 1 && (
                <div className="flex flex-wrap-reverse items-center justify-end gap-6">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>{formatMessage(pricingPlanMessages.simulationDay)}</span>
                        <Input
                            min={1}
                            max={days.length}
                            type="number"
                            value={typedDay}
                            onKeyDown={onDayInputKeyDown}
                            onChange={onChangedDayInput}
                            onBlur={onBlurredDayInput}
                            className="h-9 w-16 px-1 font-mono text-gray-900"
                        />
                        <span>{formatMessage(tableMessages.of)}</span>
                        <span className="font-mono text-gray-900">{days.length}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            type="button"
                            className="size-9"
                            onClick={() => goToDay(0)}
                            disabled={dayIndex === 0}
                        >
                            <FontAwesomeIcon icon={faChevronDoubleLeft} />
                        </Button>
                        <Button
                            variant="outline"
                            type="button"
                            className="size-9"
                            onClick={() => goToDay(dayIndex - 1)}
                            disabled={dayIndex === 0}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </Button>
                        <Button
                            variant="outline"
                            type="button"
                            className="size-9"
                            onClick={() => goToDay(dayIndex + 1)}
                            disabled={dayIndex >= days.length - 1}
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </Button>
                        <Button
                            variant="outline"
                            type="button"
                            className="size-9"
                            onClick={() => goToDay(days.length - 1)}
                            disabled={dayIndex >= days.length - 1}
                        >
                            <FontAwesomeIcon icon={faChevronDoubleRight} />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PricingSimulationTable;
